import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ReportExportAsService } from '../report-export-as.service';
import { FilterItem, FilterItemOption } from '../models/filter-item.interface';
import {
  DVFormGroup,
  DVFormControl,
  DVFormArray,
} from 'src/app/shared/helpers/dynamic-validators/dynamic-validator';
import { ReportUtils, RSelectionModel } from '../report-utils';
import { FilterCallback } from '../models/filter-callback.interface';
import { AbstractControl } from '@angular/forms';
import { RowAction } from '../models/row-actions.interface';
import { RowActionCallback } from '../models/row-action-callback.interface';
import { TableAction } from '../models/table-actions.interface';
import { PaginationObjectModel } from '../models/pagination-object.interface';
import { MatPaginator, MatTableDataSource, PageEvent } from '@angular/material';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'app-base-report',
  templateUrl: './base-report.component.html',
  styleUrls: ['./base-report.component.scss'],
})
export class BaseReportComponent implements OnInit {
  constructor(
    private exportAsService: ReportExportAsService,
    private cdk: ChangeDetectorRef
  ) {}
  // inputs

  @Input() reportName?: string;
  @Input() filterFields?: Array<FilterItem>;
  @Input() paginateLocally? = true;
  @Input() tableColumns: Array<string>;
  @Input() displayColumnNames: object;
  @Input() actionsOnDisplayedDataOnly? = false;
  @Input() defaultDisplayedColumns?: Array<string>;
  @Input() hasExports? = true;
  @Input() hasSorts? = false;
  @Input() hasSelections? = false;
  @Input() hasSearch? = false;
  @Input() hasSettings? = true;
  @Input() rowActions?: Array<RowAction>;
  @Input() tableActions?: Array<TableAction>;
  @Input() paginationObject?: PaginationObjectModel;
  @Input() alternateView?: TemplateRef<any>;
  @Input() showApplyButton? = true;
  @Input() defaultView?: 'table' | 'other';
  @Input()
  set dataArray(arr: Array<any>) {
    if (arr) {
      // console.log(arr);
      this._dataArray = arr;
      if (this.paginateLocally) {
        this.initPagination('local');
      } else {
        this.initPagination('server');
      }
    } else {
      this._dataArray = [];
    }
  }
  get dataArray() {
    return this.paginateDataArray(this.applySearch());
  }

  // outputs
  @Output() filter = new EventEmitter<FilterCallback>();
  @Output() paginationChanged = new EventEmitter<any>();
  @Output() rowActionCB = new EventEmitter<RowActionCallback>();
  @Output() selectionChange = new EventEmitter<any>();
  @Output() selectionActionCB = new EventEmitter<any>();
  // view childs
  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  @ViewChild('matTable', { static: false }) matTable: MatTableDataSource<any>;
  // public vars
  tableConfigVisible = false;
  filterForm: DVFormGroup;
  hasFilter: boolean;
  hasRowActions: boolean;
  _displayedColumns = [];
  _dataArray: Array<any> = [];
  selection: RSelectionModel;
  searchModel: string;
  currentView: 'table' | 'other';

  _paginationObject: PaginationObjectModel = {
    totalLength: 0,
    currentLength: 10,
    currentPageIndex: 0,
  };

  // private vats
  private defaultPageSize = 10;

  // getters & setters
  get displayedColumns() {
    let output = this._displayedColumns;
    if (this.hasRowActions) {
      output = [...output, '__actions'];
    }
    if (this.hasSelections) {
      output = ['__selection', ...output];
    }
    return output;
  }
  set displayedColumns(arr: Array<string>) {
    this._displayedColumns = arr;
  }

  switchView() {
    this.currentView = this.currentView === 'table' ? 'other' : 'table';
  }

  // public functions
  ngOnInit() {
    this.initVars();
    this.displayedColumns = this.defaultDisplayedColumns
      ? this.defaultDisplayedColumns
      : this.tableColumns;
    if (this.hasFilter) {
      this.filterForm = new DVFormGroup(
        ReportUtils.filterArrayToFormArray(this.filterFields)
      );
      this.filterForm.valueChanges.subscribe((value) => {
        this.filter.next({
          filter: this.filterForm.getCleanValue(),
          trigger: 'formChange',
          triggerType: 'ValueChange',
        });
      });
    }

    if (this.paginateLocally) {
      this.initPagination('local');
    } else {
      this.initPagination('server');
    }
    if (this.paginator) {
      this.matTable.paginator = this.paginator;
    }
  }

  hasHeader() {
    return this.hasExports || this.hasSettings;
  }

  print() {
    this.exportAsService.print();
  }

  pdf() {
    this.exportAsService.pdf(this.getDataArray());
  }

  csv() {
    this.exportAsService.csv(this.getDataArray(), 'trips');
  }

  xlsx() {
    this.exportAsService.xlsx(this.getDataArray(), 'trips');
  }

  paginationCB(e: PageEvent) {
    this.paginationChanged.emit(e);
    if (this.paginateLocally) {
      // console.log(e);
      this._paginationObject.currentLength = e.pageSize;
      this._paginationObject.currentPageIndex = e.pageIndex;
      this.paginator.pageSize = e.pageSize;
      this.paginator.pageIndex = e.pageIndex;
      // console.log(this._paginationObject);
      this.cdk.detectChanges();
    }
  }

  filterClickHandler() {
    this.filter.next({
      filter: this.filterForm.value,
      triggerType: 'ApplyClick',
      trigger: 'apply',
    });
  }

  initPagination(mode: 'local' | 'server') {
    if (mode === 'local') {
      this._paginationObject.totalLength = this._dataArray.length;
      //  console.log(this._paginationObject)
      this.paginateDataArray(this._dataArray);
      //  this._paginationObject.currentLength =
      //   this._dataArray.length <= this.defaultPageSize
      //     ? this._dataArray.length
      //     : this.defaultPageSize;
    } else if (mode === 'server' && this.paginationObject) {
      this._paginationObject = this.paginationObject;
    } else {
      throw new Error(
        'You must either set the pagination mode to locally or send a pagination object!'
      );
    }
  }

  rowActionHandler(actionSlug: string, causedFor: any) {
    this.rowActionCB.emit({
      action: actionSlug,
      on: causedFor,
    });
  }

  tableActionHandler(actionSlug: string) {
    this.selectionActionCB.emit({
      action: actionSlug,
      selected: this.selection.selected,
    });
  }

  // private functions
  private getDataArray() {
    if (this.actionsOnDisplayedDataOnly) {
      return this.dataArray.map((dataRow) => {
        const newRow: any = {};
        for (const key in dataRow) {
          if (this.displayedColumns.includes(key)) {
            newRow[key] = dataRow[key];
          }
        }
        return newRow;
      });
    } else {
      return this.dataArray;
    }
  }

  private applySearch() {
    if (this.searchModel) {
      return this._dataArray.filter((object) => {
        for (const prop in object) {
          if (
            object[prop] &&
            (<string>object[prop].toString().toLowerCase()).includes(
              this.searchModel.toLowerCase()
            )
          ) {
            return true;
          }
        }
        return false;
      });
    } else {
      return this._dataArray;
    }
  }

  private initVars() {
    this.currentView = this.defaultView || 'other';
    // hasFilter
    if (this.filterFields && this.filterFields.length !== 0) {
      this.hasFilter = true;
    } else {
      this.hasFilter = false;
    }

    // hasRowActions
    if (this.rowActions && this.rowActions.length !== 0) {
      this.hasRowActions = true;
    } else {
      this.hasRowActions = false;
    }

    if (this.hasSelections) {
      this.selection = new RSelectionModel<any>(this.dataArray, true, []);
      this.selection.changed.subscribe((change) => {
        this.selectionChange.emit(change);
      });
    }
  }

  private paginateDataArray(data: Array<any>) {
    if (data.length <= this._paginationObject.currentLength) {
      return data;
    } else {
      // console.log(data);
      // console.log(this._paginationObject)
      const result = data.slice(
        this._paginationObject.currentPageIndex *
          this._paginationObject.currentLength,
        this._paginationObject.currentPageIndex *
          this._paginationObject.currentLength +
          this._paginationObject.currentLength
      );
      // console.log(result);
      return result;
    }
  }

  getColumnDisplayLabel(columnName) {
    if (this.displayColumnNames && this.displayColumnNames[columnName]) {
      return this.displayColumnNames[columnName];
    } else {
      return _.startCase(columnName);
    }
  }

  toggleFilterSelectAll(
    formControl: DVFormControl | DVFormArray | DVFormGroup,
    AllValues: Array<FilterItemOption>,
    item
  ) {
    const currentLength = formControl.value.length;
    if (currentLength < AllValues.length) {
      // select all
      formControl.setValue(AllValues.map((i) => i.value));
      item._selected = true;
    } else {
      // deselect all
      formControl.setValue([]);
      item._selected = false;
    }
  }

  testChange(e) {
    // console.log('test change', e)
  }
}
